import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRight, RefreshCw } from "lucide-react";
import api from "../../lib/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FIELD_LABELS = {
  glass_spec: {
    title: "Normalizar tipos de vidrio",
    description: "Variantes equivalentes (mayúsculas, espacios, texto en chino, descripciones) agrupadas por espesor. Edita el nombre final si lo deseas; se aplica en Fábrica (F) y Lista de Precio Venta (V).",
    loading: "Analizando vidrios…",
    empty: "Todos los vidrios ya están normalizados.",
    error: "Error al analizar vidrios",
  },
  color: {
    title: "Normalizar colores",
    description: "Sinónimos y variantes (Black / BLACK / 黑色 Black → Negro; White / BLANCO → Blanco…) agrupados en un solo nombre. Edita el nombre final si lo deseas; se aplica en Fábrica (F) y Lista de Precio Venta (V).",
    loading: "Analizando colores…",
    empty: "Todos los colores ya están normalizados.",
    error: "Error al analizar colores",
  },
};

const GlassGroupRow = ({ group, draft, onDraftChange, checked, onToggle }) => (
  <div className={`rounded-lg border p-3 ${checked ? "border-blue-300 bg-blue-50/40" : "border-slate-200 opacity-70"}`} data-testid={`glass-group-${group.key}`}>
    <div className="flex items-center gap-3 mb-2">
      <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`glass-group-check-${group.key}`} />
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <span className="text-xs text-slate-500 shrink-0">{group.values.length} variante{group.values.length === 1 ? "" : "s"}</span>
        <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
        <Input
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          className="h-8 text-sm font-medium"
          data-testid={`glass-group-target-${group.key}`}
        />
      </div>
      <Badge variant="secondary" className="shrink-0">{group.total_types} tipos</Badge>
    </div>
    <div className="flex flex-wrap gap-1.5 pl-7">
      {group.values.map((v) => (
        <span key={v.raw} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 rounded px-2 py-0.5 max-w-full" title={v.raw}>
          <span className="truncate max-w-[260px]">{v.raw}</span>
          <span className="text-slate-400">· F{v.factory_count}{v.sales_count ? ` V${v.sales_count}` : ""}</span>
        </span>
      ))}
    </div>
  </div>
);

export const GlassNormalizationDialog = ({ open, onOpenChange, factoryPassword, onApplied, field = "glass_spec" }) => {
  const labels = FIELD_LABELS[field] || FIELD_LABELS.glass_spec;
  const [groups, setGroups] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`${API}/factory/glass-normalization/preview`, {
        params: { field },
        headers: { "X-Factory-Password": factoryPassword },
      });
      setGroups(res.data.groups);
      setDrafts(Object.fromEntries(res.data.groups.map((g) => [g.key, g.proposed])));
      setSelected(new Set(res.data.groups.map((g) => g.key)));
    } catch (error) {
      toast.error(error.response?.data?.detail || labels.error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open, field]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const selectedGroups = groups.filter((g) => selected.has(g.key) && drafts[g.key]?.trim());
  const affected = selectedGroups.reduce((s, g) => s + g.total_types, 0);

  const handleApply = async () => {
    setIsApplying(true);
    try {
      const res = await api.post(
        `${API}/factory/glass-normalization/apply`,
        { field, mappings: selectedGroups.map((g) => ({ from_values: g.values.map((v) => v.raw), to_value: drafts[g.key].trim() })) },
        { headers: { "X-Factory-Password": factoryPassword } }
      );
      toast.success(res.data.message);
      onOpenChange(false);
      onApplied?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al normalizar vidrios");
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="glass-normalization-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-violet-600" /> {labels.title}</span>
            <Button size="sm" variant="ghost" onClick={load} disabled={isLoading} data-testid="glass-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            </Button>
          </DialogTitle>
          <DialogDescription>
            {labels.description}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> {labels.loading}</div>
          ) : groups.length === 0 ? (
            <p className="text-center text-slate-500 py-10" data-testid="glass-no-groups">{labels.empty}</p>
          ) : groups.map((g) => (
            <GlassGroupRow
              key={g.key}
              group={g}
              draft={drafts[g.key] ?? ""}
              onDraftChange={(v) => setDrafts((d) => ({ ...d, [g.key]: v }))}
              checked={selected.has(g.key)}
              onToggle={() => toggle(g.key)}
            />
          ))}
        </div>

        <DialogFooter className="sm:justify-between items-center">
          <span className="text-sm text-slate-500" data-testid="glass-summary">
            {selectedGroups.length} grupo{selectedGroups.length === 1 ? "" : "s"} · {affected} tipos se renombrarán
          </span>
          <Button onClick={handleApply} disabled={isApplying || selectedGroups.length === 0} className="bg-violet-600 hover:bg-violet-700" data-testid="glass-apply-btn">
            {isApplying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Aplicar normalización
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GlassNormalizationDialog;
